<template>
  <div
    v-if="show"
    class="add-contact-sidebar d-flex flex-column"
  >
    <div class="d-flex justify-content-between align-items-center px-3 px-lg-4 pt-3 pt-lg-4">
      <h4 class="mb-0">
        {{ $t(`${tt}.modal.${isExist ? 'edit' : 'add'}`) }}
      </h4>
      <div class="user-chat-nav">
        <button
          type="button"
          class="btn nav-btn"
          @click="show = false"
        >
          <i class="ri-close-line" />
        </button>
      </div>
    </div>

    <div class="flex-fill overflow-auto virtual-list pb-0">
      <template v-if="isExist && !editMode">
        <div class="px-3 px-lg-4 pt-3 pt-lg-4">
          <div class="card border custom-accordion">
            <div class="card-header">
              <h5 class="font-size-14 m-0">
                <i class="ri-user-2-line mr-2 align-middle d-inline-block" />
                {{ $t(`${tt}.modal.about`) }}
              </h5>
            </div>
            <div class="collapse show">
              <div class="card-body position-relative">
                <div
                  class="position-absolute"
                  style="right: 24px; top: 24px;"
                >
                  <button
                    type="button"
                    class="btn btn-light btn-sm"
                    @click="editMode = true"
                  >
                    <i class="ri-edit-fill mr-1 align-middle" />
                    {{ $t('home.edit') }}
                  </button>
                </div>
                <show-fields :params="params" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <form
          :data-vv-scope="formName"
          class="pt-4 pb-0 pl-4 pr-0 form-horizontal"
          style="color: #495057;"
          @submit.prevent="onSubmit"
        >
          <form-fields
            v-model="params"
            :form-name="formName"
          />
        </form>
      </template>
    </div>

    <div
      v-if="!(isExist && !editMode)"
      class="w-100 text-right px-3 px-lg-4 pt-1 pt-lg-2 pb-3 pb-lg-4"
    >
      <button
        type="button"
        :disabled="submit || !contactGroupId.length"
        class="btn ml-auto btn-primary btn-labeled btn-labeled-left"
        @click="onSubmit"
      >
        <b><i :class="[submit ? 'ri-loader-2-line spinner' : 'icon-checkmark4']" /></b>
        {{ $t(`home.save`) }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {alert} from '@pnotify/core';
import catchDispatch from "@/utils/catchDispatch";
import {params} from '@/components/Room/Header/AddContact/ConstAddContactHeaderRoomComponents';
import ShowFields from '@/components/Room/Header/AddContact/FieldsShowAddContactHeaderRoomComponents';
import FormFields from '@/components/Room/Header/AddContact/FieldsFormAddContactHeaderRoomComponents';

export default {
  components: {
    ShowFields,
    FormFields,
  },
  inject: ['$validator'],
  data() {
    return {
      tt: 'room.header.add_contact',
      formName: 'room.header.add_contact.form',
      submit: false,
      params: {},
    };
  },
  computed: {
    editMode: {
      get() {
        return this.$store.state.addContact.editMode;
      },
      set(editMode) {
        this.$store.commit('addContact/SET_EDIT_MODE', editMode);
      },
    },
    show: {
      get() {
        return this.$store.state.addContact.show;
      },
      set(show) {
        this.$store.commit('addContact/SET_SHOW', show);
      },
    },
    room() {
      return this.$store.state.addContact.room;
    },
    contactGroupId() {
      return this.$store.state.rooms.setting.contact_group_id;
    },
    disabled() {
      return !this.contactGroupId.length;
    },
    isExist() {
      return !!this.room.contact;
    },
  },
  watch: {
    show(show) {
      if (show) {
        this.onShow();
      } else {
        this.$store.commit('addContact/RESET');
      }
    },
    room: {
      deep: true,
      handler() {
        this.show && this.onShow();
      },
    },
  },
  methods: {
    onShow() {
      const {phone_number: phone, contact: c} = this.room;
      this.params = Object.keys(params).reduce((r, k) => {
        if (['phone'].includes(k)) return r;
        r[k] = (c && c[k] && c[k].length) ? c[k] : '';
        return r;
      }, {phone});
    },
    async onSubmit() {
      if (this.submit) return false;
      this.submit = true;

      this.errors.clear(this.formName);
      const r = await this.$validator.validateAll(this.formName);
      if (!r) {
        this.submit = false;
        return;
      }

      const data = {
        text_data: {text: [], separate_symbol: 'comma'},
        ordering: {},
      };

      let ordering = 1;
      Object.keys(params).forEach(k => {
        data.ordering[k] = 0;
        if (!this.params[k].length) return;
        data.ordering[k] = ordering++;
        data.text_data.text.push(this.params[k]);
      });
      data.text_data.text = data.text_data.text.join(',');

      const gid = this.contactGroupId;

      try {
        // Upload text file
        const file = new File([data.text_data.text], 'file_from_textarea.txt', {type: 'text/plain'});
        const fileForm = new FormData();
        fileForm.append('uploadfile', file);
        const {file_id} = await axios.post(`/contacts/api/v1/files`, fileForm).then(r => r.data);

        // Set params
        const {id: process_id} = await axios.post(`/contacts/api/v1/groups/${gid}/files/${file_id}/params`, {
          "file": {
            "file_id": file_id,
            "skip_firs_raw": false,
            "separate_symbol": "comma",
            "charset": "utf-8",
          },
          "ordering": data.ordering,
        }).then(r => r.data);

        // Start processing
        await new Promise((resolve, reject) => {
          const delay = 500;
          let startCount = null;
          let failCount = 0;
          const check = async () => {
            if (failCount > 5) {
              return reject(new Error('checkProcessing | fail > 5'));
            }

            const {count} = axios
              .get(`/contacts/api/v1/groups/${gid}/files/${file_id}/processing/${process_id}`)
              .then(r => r.data);
            if (count === null) {
              failCount++;
              return setTimeout(check, delay);
            }
            failCount = 0;

            if (startCount === null) {
              startCount = count;
            }

            let processPercent = 100;
            if (startCount !== 0) {
              processPercent = ((startCount - count) / startCount) * 100;
            }

            if (processPercent < 100) {
              return setTimeout(check, delay);
            }

            return resolve(true);
          };
          check();
        });

        // Remove duplicates
        await axios.delete(`/contacts/api/v1/groups/${gid}/duplicates:remove`);

        this.$store.commit('rooms/SET_ROOMS', [{...this.room, contact: this.params}]);

        alert({
          title: this.$t('home.success'),
          text: this.$t(`${this.tt}.save_success`),
          addClass: 'bg-success text-white',
        });

        this.show = false;
      } catch (e) {
        catchDispatch.call(this, e);
      } finally {
        this.submit = false;
      }
    },
  },
};
</script>

<style scoped>

</style>
